/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * The dealer's place of work, as both dialogs that collect it understand it.
 *
 * The profile dialog owns this address and saves it; the feedback dialog only borrows it, and asks
 * for it solely when the profile has not managed to fill it in. Both need the same six fields, the
 * same ISO pickers, the same rule for which of them GDL has already answered, and the same idea of
 * when the address is complete, so all of that lives here rather than in either dialog.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserProfile } from '@/api/settings';
import {
  countriesForSubdivision,
  loadCountries,
  loadSubdivisions,
  normaliseCountry,
  normaliseSubdivision,
  type GeoOption,
} from '@/lib/geoOptions';
import { resolve, type FieldSource, type ResolvedField } from '@/lib/profileField';
import { useSeededForm } from '@/lib/useSeededForm';

/** The address in the order it is asked for, which is also the order it is rendered in. */
export const ADDRESS_KEYS = [
  'dealerName',
  'address',
  'country',
  'state',
  'city',
  'postalCode',
] as const;

export type AddressKey = (typeof ADDRESS_KEYS)[number];

export interface DealershipAddress {
  fields: Record<AddressKey, ResolvedField>;
  /** Trimmed values in submission form, whoever supplied them. */
  values: Record<AddressKey, string>;
  change: (key: AddressKey) => (value: string) => void;
  changeCountry: (value: string) => void;
  countryOptions: GeoOption[];
  subdivisions: GeoOption[];
  selectedCountry: string;
  /** False where the chosen country has no ISO subdivisions, so no state can be asked for. */
  stateApplies: boolean;
  /** Required fields still blank. Empty means the address is good enough to submit. */
  missing: AddressKey[];
  complete: boolean;
  /** GDL answered none of it, which is the only case worth telling the user about. */
  gdlSuppliedNothing: boolean;
  anyValue: boolean;
  /**
   * Whether the stored address has finished folding onto ISO codes. Until it has, the country and
   * state read blank, so a caller that decides whether to ask for an address at all must wait:
   * otherwise it puts the fields up and takes them away again a frame later.
   */
  ready: boolean;
}

const NO_OPTIONS: GeoOption[] = [];
const NO_COUNTRIES: string[] = [];

/**
 * The result of `load(key)` for the current key, or `fallback` while there is no key to load or
 * nothing has come back for it yet.
 *
 * The result is kept with the key it belongs to, so switching country shows an empty list until
 * that country's provinces arrive rather than the previous country's. A superseded load is dropped
 * on arrival for the same reason: the ISO tables load asynchronously, and without this the slower
 * of two requests would win by landing last.
 */
function useLatest<T>(key: string | null, load: (key: string) => Promise<T>, fallback: T): T {
  const [loaded, setLoaded] = useState<{ key: string; value: T } | null>(null);
  useEffect(() => {
    if (key === null) return undefined;
    let current = true;
    void load(key).then((value) => {
      if (current) setLoaded({ key, value });
    });
    return () => {
      current = false;
    };
  }, [key, load]);
  return loaded?.key === key ? loaded.value : fallback;
}

/**
 * The dealership address behind whichever dialog is asking, resolved against what GDL already
 * knows and constrained to ISO 3166 wherever the user has to supply it themselves.
 *
 * `enabled` gates the ISO downloads: only dealers are ever shown an address, and the tables are a
 * quarter of a megabyte that nobody else should pay for.
 */
export function useDealershipAddress(
  profile: UserProfile | undefined,
  enabled: boolean,
): DealershipAddress {
  const { i18n } = useTranslation();

  // A stored country or state folds onto its ISO code, so a row saved as "ONTARIO" or "XII" selects
  // the right entry. Anything that will not fold seeds blank: the picker asks again rather than
  // keeping a value no country agrees with. Held apart from the seed below because it arrives with
  // the ISO chunk, and the plain text fields should not wait on that.
  const storedCountry = profile?.country ?? '';
  const storedState = profile?.state ?? '';
  // Tagged with the values it was folded from, so a seed left over from the previous profile is not
  // mistaken for a settled answer about this one.
  const storedKey = `${storedCountry}\u0000${storedState}`;
  const [isoSeed, setIsoSeed] = useState<{ key: string; country: string; state: string } | null>(
    null,
  );
  useEffect(() => {
    if (!enabled) return undefined;
    let current = true;
    void (async () => {
      const country = (await normaliseCountry(storedCountry)) ?? '';
      const state = (await normaliseSubdivision(country, storedState)) ?? '';
      if (current) setIsoSeed({ key: storedKey, country, state });
    })();
    return () => {
      current = false;
    };
  }, [enabled, storedKey, storedCountry, storedState]);
  const settled = isoSeed?.key === storedKey ? isoSeed : null;

  const seed = useMemo(
    () => ({
      dealerName: profile?.dealerName ?? '',
      address: profile?.address ?? '',
      city: profile?.city ?? '',
      postalCode: profile?.postalCode ?? '',
      country: settled?.country ?? '',
      state: settled?.state ?? '',
    }),
    [profile, settled],
  );
  const { values: entered, change } = useSeededForm(seed);

  // GDL names the state far more often than the country, which used to leave a Californian
  // dealership with an open picker that would happily file it under the Bahamas. Ask ISO which
  // countries actually have that state: one answer settles it, several become the only choices.
  const fromGdl = (key: AddressKey) => {
    const field = resolve(profile, key, '');
    return field.locked ? field.value : '';
  };
  const gdlCountry = fromGdl('country');
  const gdlState = fromGdl('state');
  const stateCountries = useLatest(
    enabled && !gdlCountry && gdlState ? gdlState : null,
    countriesForSubdivision,
    NO_COUNTRIES,
  );
  const impliedCountry = stateCountries.length === 1 ? stateCountries[0] : undefined;

  // ISO lists arrive in their own chunk, so the dialog paints before ~250 countries are parsed.
  const countries = useLatest(enabled ? i18n.language : null, loadCountries, NO_OPTIONS);

  // Where the locked state belongs to a handful of countries rather than exactly one, those are the
  // only ones worth offering: "Florida" is a real answer in Puerto Rico, the US and Uruguay, and
  // nowhere else.
  const countryOptions =
    stateCountries.length > 1
      ? countries.filter((c) => stateCountries.includes(c.code))
      : countries;

  const fields: Record<AddressKey, ResolvedField> = {
    dealerName: resolve(profile, 'dealerName', entered.dealerName),
    address: resolve(profile, 'address', entered.address),
    country: impliedCountry
      ? { value: impliedCountry, locked: true, source: 'gdl' as FieldSource }
      : resolve(profile, 'country', entered.country),
    state: resolve(profile, 'state', entered.state),
    city: resolve(profile, 'city', entered.city),
    postalCode: resolve(profile, 'postalCode', entered.postalCode),
  };

  const selectedCountry = fields.country.value.trim().toUpperCase();
  const subdivisions = useLatest(
    enabled && selectedCountry ? selectedCountry : null,
    loadSubdivisions,
    NO_OPTIONS,
  );

  // Provinces belong to a country, so switching country drops a selection that no longer exists.
  const changeCountry = (value: string) => {
    change('country')(value);
    change('state')('');
  };

  // A locked field is never missing: the system of record has already answered for it. State is
  // exempt where the chosen country has no subdivisions on file, which would otherwise ask for
  // something the picker cannot offer.
  const stateApplies = subdivisions.length > 0;
  const missing = ADDRESS_KEYS.filter(
    (key) => (key !== 'state' || stateApplies) && !fields[key].value.trim(),
  );

  return {
    fields,
    values: Object.fromEntries(
      ADDRESS_KEYS.map((key) => [key, fields[key].value.trim()]),
    ) as Record<AddressKey, string>,
    change,
    changeCountry,
    countryOptions,
    subdivisions,
    selectedCountry,
    stateApplies,
    missing,
    complete: missing.length === 0,
    gdlSuppliedNothing: ADDRESS_KEYS.every((key) => !fields[key].locked),
    anyValue: ADDRESS_KEYS.some((key) => fields[key].value.trim() !== ''),
    ready: !enabled || settled !== null,
  };
}
